import type { StreamEvent } from './streamReducer'

/**
 * Parses an SSE stream from Foundry's Responses API into typed
 * `StreamEvent`s the reducer can consume.
 *
 * The Responses API replaces the classic Assistants/Threads/Runs API.
 * Event names follow the OpenAI Responses event taxonomy:
 *
 *     event: response.created
 *     data: {"response": {...}}
 *
 *     event: response.output_text.delta
 *     data: {"item_id": "...", "delta": "Hello "}
 *
 *     event: response.completed
 *     data: {"response": {...}}
 *
 * Reference:
 *   https://learn.microsoft.com/rest/api/aifoundry/aiproject#responses
 *   https://platform.openai.com/docs/api-reference/responses-streaming
 *
 * Frames are separated by blank lines; a frame can have multiple `data:`
 * lines which the spec says to concatenate with newlines. We rely on
 * Foundry emitting one data line per frame (true in practice), but
 * defensively handle multi-line data too.
 *
 * The browser's `EventSource` API would parse this for us — but it
 * only does GET requests and Foundry's responses endpoint is POST. So
 * we parse the response body as `ReadableStream<Uint8Array>` ourselves.
 */
export async function* parseSseStream(
  body: ReadableStream<Uint8Array>,
): AsyncIterableIterator<StreamEvent> {
  const reader = body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE frames end with double-newline. Process every complete frame
      // and keep the remainder in the buffer.
      let frameEnd: number
      while ((frameEnd = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, frameEnd)
        buffer = buffer.slice(frameEnd + 2)
        const event = parseFrame(frame)
        if (event) yield event
      }
    }
  } finally {
    reader.releaseLock()
  }
}

function parseFrame(frame: string): StreamEvent | null {
  let eventName: string | null = null
  const dataLines: string[] = []

  for (const rawLine of frame.split('\n')) {
    const line = rawLine.trimEnd()
    if (!line || line.startsWith(':')) continue // empty or comment
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim())
    }
  }

  if (!eventName || dataLines.length === 0) return null

  let data: unknown
  try {
    data = JSON.parse(dataLines.join('\n'))
  } catch {
    return null
  }

  return mapFoundryEvent(eventName, data)
}

// ---------------------------------------------------------------------------
// Foundry Responses API event-name → StreamEvent mapping
//
// The Responses API streams a different event taxonomy from the classic
// Threads/Runs API. We keep the reducer's StreamEvent variants stable
// (so the render layer doesn't change) and map the new event names to
// them here. Anything not listed returns null and the reducer never
// sees it.
//
// Mapping summary:
//   response.created                          -> thread.run.created (carries response.id; we use it as
//                                                 the "thread" id for chaining via previous_response_id)
//   response.output_item.added (message)      -> thread.message.created
//   response.output_text.delta                -> thread.message.delta
//   response.output_item.done (message)       -> thread.message.completed
//   response.output_item.added (mcp_call)     -> thread.run.step.tool_call_started
//   response.mcp_call_arguments.delta         -> thread.run.step.tool_call_args_delta
//   response.output_item.done (mcp_call)      -> thread.run.step.tool_call_completed
//   response.completed                        -> thread.run.completed
//   response.failed                           -> thread.run.failed
//   response.error                            -> stream.error (terminal stream error)
//
// The agent's MCP tools run with `requireApproval: "never"` and it has no
// function tools (`infra/agents/keystone-assistant.agent.json`), so the
// approval, OAuth-consent and `function_call` items are not mapped.
// ---------------------------------------------------------------------------

interface ResponseEnvelope {
  response?: {
    id?: string
    error?: { message?: string } | null
  }
}

interface OutputItemPayload {
  item?: {
    id?: string
    type?: string
    name?: string
    output?: string
  }
}

interface TextDeltaPayload {
  item_id?: string
  delta?: string
}

interface McpArgsDeltaPayload {
  item_id?: string
  delta?: string
}

interface ResponseErrorPayload {
  error?: { message?: string }
  message?: string
}

function mapFoundryEvent(eventName: string, data: unknown): StreamEvent | null {
  if (!isObject(data)) return null

  switch (eventName) {
    case 'response.created': {
      const p = data as ResponseEnvelope
      const id = p.response?.id
      if (typeof id !== 'string') return null
      // Single dispatch carries the response id forward; the reducer uses
      // it both as the conversation-chain key and as the run status flip.
      return { kind: 'thread.run.created', runId: id }
    }

    case 'response.in_progress':
      // No-op — covered by output_item.added handling for the actual
      // message/tool affordances.
      return null

    case 'response.output_item.added': {
      const p = data as OutputItemPayload
      const item = p.item
      if (!item || typeof item.id !== 'string') return null
      if (item.type === 'message') {
        return { kind: 'thread.message.created', messageId: item.id }
      }
      // MCP tool calls surface as `mcp_call` items. Their `name` is the
      // tool name; arguments stream via separate delta events.
      if (item.type === 'mcp_call') {
        const toolName = item.name ?? ''
        if (!toolName) return null
        return {
          kind: 'thread.run.step.tool_call_started',
          stepId: item.id,
          toolName,
        }
      }
      // `mcp_list_tools` and other informational items are intentionally
      // ignored — they're internal Foundry plumbing, not user-visible.
      return null
    }

    case 'response.output_text.delta': {
      const p = data as TextDeltaPayload
      if (typeof p.item_id !== 'string' || typeof p.delta !== 'string') return null
      return { kind: 'thread.message.delta', messageId: p.item_id, textDelta: p.delta }
    }

    case 'response.output_text.done': {
      const p = data as TextDeltaPayload
      if (typeof p.item_id !== 'string') return null
      return { kind: 'thread.message.completed', messageId: p.item_id }
    }

    case 'response.mcp_call_arguments.delta': {
      const p = data as McpArgsDeltaPayload
      if (typeof p.item_id !== 'string' || typeof p.delta !== 'string') return null
      return { kind: 'thread.run.step.tool_call_args_delta', stepId: p.item_id, argsDelta: p.delta }
    }

    case 'response.output_item.done': {
      const p = data as OutputItemPayload
      const item = p.item
      if (!item || typeof item.id !== 'string') return null
      if (item.type === 'mcp_call') {
        // MCP tool: Foundry executed it; `output` carries the result.
        return {
          kind: 'thread.run.step.tool_call_completed',
          stepId: item.id,
          resultJson: item.output ?? '',
        }
      }
      if (item.type === 'message') {
        return { kind: 'thread.message.completed', messageId: item.id }
      }
      return null
    }

    case 'response.completed':
      return { kind: 'thread.run.completed' }

    case 'response.failed': {
      const p = data as ResponseEnvelope
      const msg = p.response?.error?.message ?? 'Response failed'
      return { kind: 'thread.run.failed', message: msg }
    }

    case 'response.error': {
      const p = data as ResponseErrorPayload
      const msg = p.error?.message ?? p.message ?? 'Stream error'
      return { kind: 'stream.error', message: msg }
    }

    default:
      return null
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}
